import {
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
    type RefObject,
} from 'react';

interface UseScrollArrowsReturn {
    scrollRef: RefObject<HTMLElement | null>;
    canScrollLeft: boolean;
    canScrollRight: boolean;
    prefersReducedMotion: boolean;
}

/**
 * Hook that tracks horizontal scroll position of a container.
 * Uses requestAnimationFrame to batch scroll state updates for performance.
 * Respects prefers-reduced-motion for accessibility.
 */
export const useScrollArrows = (): UseScrollArrowsReturn => {
    const scrollRef = useRef<HTMLElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const prefersReducedMotion = useMemo(
        () =>
            typeof window !== 'undefined'
                ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
                : false,
        [],
    );

    const updateScrollState = useCallback(() => {
        requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (!el) return;
            const { scrollLeft, scrollWidth, clientWidth } = el;
            setCanScrollLeft(scrollLeft > 5);
            setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 5);
        });
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;

        updateScrollState();

        el.addEventListener('scroll', updateScrollState, { passive: true });
        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(el);

        return () => {
            el.removeEventListener('scroll', updateScrollState);
            resizeObserver.disconnect();
        };
    }, [updateScrollState]);

    return {
        scrollRef,
        canScrollLeft,
        canScrollRight,
        prefersReducedMotion,
    };
};
